import type React from "react";
import { Link } from "react-router";
import { twJoin } from "tailwind-merge";

import { useImageResolver } from "@/api/hooks/useImageResolver";
import type { ImageDto } from "@/api/types";
import { cardSurfaceVariants } from "@/design-system/Card";
import { Inline } from "@/design-system/Inline";
import { Spread } from "@/design-system/Spread";

import { ContextMenu, type ContextMenuItem } from "../ContextMenu/ContextMenu";

interface EntityCardProps {
  title: string;
  subtitle?: string;
  href?: string;
  onClick?: () => void;
  bannerImg: ImageDto | undefined;
  "data-testid"?: string;
  menuActions?: ContextMenuItem[];
  metadata?: string;
  renderBadges?: () => React.ReactNode;
  renderCtaAction?: () => React.ReactNode;
  renderLeftAction?: () => React.ReactNode;
}

export function EntityCard({
  title,
  subtitle,
  href,
  "data-testid": dataTestId,
  menuActions,
  bannerImg,
  metadata,
  renderBadges,
  renderCtaAction,
  renderLeftAction,
  onClick,
}: EntityCardProps): React.ReactNode {
  const resolveImage = useImageResolver();

  const Badges = renderBadges ? renderBadges() : null;
  let Component: any = "div";
  if (href) {
    Component = Link;
  } else if (onClick) {
    Component = "button";
  }

  return (
    <Component
      {...(href ? { to: href } : {})}
      onClick={onClick}
      data-testid={dataTestId}
      className={twJoin(
        "flex w-full flex-col overflow-hidden",
        cardSurfaceVariants({ interactive: Boolean(href || onClick) }),
      )}
    >
      <header className="relative w-full">
        {Badges && (
          <aside className="absolute top-card-compact left-card-compact z-raised flex flex-wrap gap-space-xs">
            {Badges}
          </aside>
        )}
        {metadata && (
          <span className="absolute top-card-compact right-card-compact z-raised rounded-sm bg-background-default-inverse/50 px-space-sm py-space-xs text-overline text-text-default-inverse">
            {metadata}
          </span>
        )}
        {bannerImg ? (
          <img
            src={resolveImage(bannerImg, "intrinsic")}
            alt={`${title}-banner`}
            className="aspect-video w-full bg-background-on-default object-cover"
          />
        ) : (
          <div className="aspect-video w-full bg-background-on-default object-cover" />
        )}
      </header>
      <section className="relative flex flex-1 flex-col gap-space-lg p-card-compact">
        <header className="mt-auto flex flex-col gap-space-xs">
          <Spread space="md">
            <h4 className="truncate text-heading-4">{title}</h4>
            {menuActions && <ContextMenu items={menuActions} />}
          </Spread>
          {subtitle && <p className="truncate text-caption text-text-secondary">{subtitle}</p>}
        </header>
        {(renderLeftAction || renderCtaAction) && (
          <Inline gap="md" align="end" wrap={false}>
            {renderLeftAction && <span>{renderLeftAction()}</span>}
            {renderCtaAction && <span className="ml-auto">{renderCtaAction()}</span>}
          </Inline>
        )}
      </section>
    </Component>
  );
}
